import type { CryptoSwapsTranslations } from "@/shared/lang/interfaces/cryptoSwaps";

import {
  DEPOSIT_ADDRESS_SAFETY_MARGIN_MS,
  SELL_FAILURE_REASON_KEYS,
  SELL_NON_TERMINAL_STATUSES,
  SELL_STATUS_PILL,
  type SellPillVariant,
} from "../constants/cryptoSwaps.constants";
import type {
  OtcSellDeposit,
  OtcSellOrder,
  OtcSellOrderStatus,
  OtcSellQuote,
} from "../types/cryptoSell.types";
import type { OtcAmount } from "../types/cryptoSwaps.types";

/** Drives the polling and the pulsing dot. An expiry is still watched server-side, but nothing waits on it. */
export function isLiveSale(status: OtcSellOrderStatus): boolean {
  return SELL_NON_TERMINAL_STATUSES.includes(status);
}

export function sellPillVariant(status: OtcSellOrderStatus): SellPillVariant {
  return SELL_STATUS_PILL[status] ?? "neutral";
}

/**
 * Only while the deposit is awaited: a second transfer after that would not be part of the sale. The slip
 * itself decides whether the address is still safe to show.
 */
export function showsDepositInstructions(order: Pick<OtcSellOrder, "status">): boolean {
  return order.status === "AWAITING_DEPOSIT";
}

/** When the slip withdraws the address, or null when the sale gives no usable deadline. */
export function depositAddressDeadlineMs(deposit: Pick<OtcSellDeposit, "expires_at">): number | null {
  const expiresAt = deposit.expires_at ? Date.parse(deposit.expires_at) : Number.NaN;
  return Number.isFinite(expiresAt) ? expiresAt - DEPOSIT_ADDRESS_SAFETY_MARGIN_MS : null;
}

/** The address to show at `nowMs`, failing closed on a missing address, a missing deadline or a late clock. */
export function usableDepositAddress(
  deposit: Pick<OtcSellDeposit, "address" | "expires_at">,
  nowMs: number,
): string | null {
  const address = deposit.address?.trim() ?? "";
  const deadlineMs = depositAddressDeadlineMs(deposit);
  if (!address || deadlineMs === null || nowMs >= deadlineMs) return null;
  return address;
}

export function sellFailureReasonKey(reason: string | null | undefined): keyof CryptoSwapsTranslations {
  return (reason ? SELL_FAILURE_REASON_KEYS[reason] : undefined) ?? "sell_reason_generic";
}

/** The COP field shows what the key is paid, the leg `receive_amount` fixes; the net is the ticket's. */
export function sellConverterQuote(
  quote: Pick<OtcSellQuote, "send" | "proceeds"> | null,
): { send: OtcAmount; receive: OtcAmount } | null {
  return quote ? { send: quote.send, receive: quote.proceeds } : null;
}

export type SellStepId =
  | "created"
  | "received"
  | "cop_sent"
  | "credited"
  | "expired"
  | "refunding"
  | "refunded";

export type SellStepState = "done" | "current" | "todo" | "failed";

export interface SellStep {
  id: SellStepId;
  state: SellStepState;
  at: string | null;
}

const HAPPY_PATH: SellStepId[] = ["created", "received", "cop_sent", "credited"];

const REACHED: Record<string, number> = {
  AWAITING_DEPOSIT: 1,
  PROCESSING: 2,
  COP_SENT: 3,
  COMPLETED: 4,
};

/** Each step from the sale's own timestamps; the first step not reached yet is the one in progress. */
export function sellTimeline(order: OtcSellOrder): SellStep[] {
  const at: Record<SellStepId, string | null> = {
    created: order.created_at,
    received: order.deposit_received_at,
    cop_sent: order.cop_sent_at,
    credited: order.completed_at,
    expired: order.expired_at,
    refunding: null,
    refunded: order.refunded_at,
  };
  const created: SellStep = { id: "created", state: "done", at: at.created };
  const received: SellStep[] = order.deposit_received_at
    ? [{ id: "received", state: "done", at: at.received }]
    : [];

  if (order.status === "EXPIRED") return [created, { id: "expired", state: "failed", at: at.expired }];
  if (order.status === "REFUNDING")
    return [created, ...received, { id: "refunding", state: "current", at: null }];
  if (order.status === "REFUNDED")
    return [created, ...received, { id: "refunded", state: "failed", at: at.refunded }];

  const reached = REACHED[order.status] ?? 1;
  return HAPPY_PATH.map((id, index): SellStep => {
    if (index < reached) return { id, state: "done", at: at[id] };
    if (index === reached) return { id, state: "current", at: null };
    return { id, state: "todo", at: null };
  });
}
